import { atom, atomFamily, selectorFamily } from 'recoil';
import {
  IActionFilter,
  ICheckedRecord,
  ICheckedTableItems,
  IFilterTripsOverview,
  IInfoParkingTag,
  IInfoRecord,
  IInfoRideTag,
  IIsActiveAdvanceFilter,
  ITableSettingsData,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilters,
  ITripsOverviewColumn,
  ITripsOverviewTableData,
} from './interfaces';

export const rideItemIdsAtom = atom<string[]>({
  key: 'tripsOverviewState_rideItemIds',
  default: [],
});

export const summaryItemIndexesAtom = atom<number[]>({
  key: 'tripsOverviewState_summaryItemIndexes',
  default: [],
});

export const hiddenSummaryItemIndexesAtom = atom<number[]>({
  key: 'tripsOverviewState_hiddenSummaryItemIndexes',
  default: [],
});

export const rideItemTimestampsAtom = atom<string[]>({
  key: 'tripsOverviewState_rideItemIndexes',
  default: [],
});

export const rideItemAtom = atomFamily<ITripsOverViewRecord | null, string>({
  key: 'tripsOverviewState_rideItem',
  default: null,
});

export const rideInfoRecordsAtom = atomFamily<IInfoRecord[], string>({
  key: 'tripsOverviewState_rideInfoRecords',
  default: [],
});

export const rideBookFiltersAtom = atom<string[]>({
  key: 'tripsOverviewState_ride_book_table_filters',
  default: [],
});
export const rideBookFilterAtom = atomFamily<IActionFilter | null, string>({
  key: 'tripsOverviewState_ride_book_table_filters',
  default: null,
});

export const columnsKeyOrderAtom = atom<{ key: string; hidden: boolean }[]>({
  key: 'tripsOverviewState_ride_book_column_order',
  // default: DEFAULT_ORDER_COLUMNS,
  default: [],
});
export const rideBookDataAtom = atom<ITripsOverviewTableData | null>({
  key: 'tripsOverviewState_ride_book_table_data',
  default: null,
});
export const loadingDataAtom = atom<boolean>({
  key: 'tripsOverviewState_ride_book_loading_table_data',
  default: false,
});
export const hasMoreAtom = atom<boolean>({
  key: 'tripsOverviewState_ride_book_hasmore',
  default: true,
});
export const offsetAtom = atom<number>({
  key: 'tripsOverviewState_ride_book_offset',
  default: 0,
});
export const tableSettingsStateAtom = atom<ITableSettingsData | null>({
  key: 'tripsOverviewState_ride_book_tableSettingsState',
  default: null,
});

export const checkedRecordsAtom = atom<ICheckedRecord[]>({
  key: 'tripsOverviewState_ride_book_checked_records',
  default: [],
});
export const filterForRequestAtom = atom<IFilterTripsOverview>({
  key: 'tripsOverviewState_ride_book_filter_for_request',
  default: {},
});

export const checkedTableItemsAtom = atom<ICheckedTableItems>({
  key: 'tripsOverviewState_ride_book_checked_table_items',
  default: {
    mainCheckbox: false,
    intervals: [],
    intervalsBlacklist: [],
    rideIdBlacklist: {},
    rideIds: {},
    customerId: null,
    counts: {},
  },
});
export const showSummariesAtom = atom<boolean>({
  key: 'tripsOverviewState_ride_book_summary_settings_show_summaries',
  default: true,
});
export const isSummarySettingsMenuOpenedAtom = atom<boolean>({
  key: 'tripsOverviewState_ride_book_isSummarySettingsMenuOpened',
  default: false,
});
export const isTableSettingsMenuOpenedAtom = atom<boolean>({
  key: 'tripsOverviewState_ride_book_isTableSettingsMenuOpened',
  default: false,
});
export const sortFilterAtom = atom<{ [key: string]: string }>({
  key: 'tripsOverviewState_ride_book_sortFilter',
  default: { timestamp: 'asc' },
});

export const commentAtom = atom<{ type: string; text: string } | null>({
  key: 'tripsOverviewState_ride_book_comment',
  default: null,
});
export const rideUsageAtom = atom<IInfoRideTag[]>({
  key: 'tripsOverviewState_ride_book_ride_usage',
  default: [],
});
export const parkingUsageAtom = atom<IInfoParkingTag[]>({
  key: 'tripsOverviewState_ride_book_parking_usage',
  default: [],
});
export const driverChangingAtom = atom<{ id: number; name: string | null } | null>({
  key: 'tripsOverviewState_ride_book_driver',
  default: null,
});
export const ridePrivacyAtom = atom<string>({
  key: 'tripsOverviewState_ride_book_ride_privacy',
  default: '',
});
export const driverSelector = selectorFamily<
  { name: string | null; id: number | null } | null,
  string
>({
  key: 'tripsOverviewState_driver',
  get:
    (rideId: string) =>
    ({ get }) => {
      const ride: ITripsOverViewRecord | null = get(rideItemAtom(rideId));
      if (!ride) {
        return null;
      }
      return ride.driver;
    },
});
export const customerSelector = selectorFamily<
  { name: string | null; label: string | null; id: number | null } | null,
  string
>({
  key: 'tripsOverviewState_customer',
  get:
    (rideId: string) =>
    ({ get }) => {
      const ride: ITripsOverViewRecord | null = get(rideItemAtom(rideId));
      if (!ride) {
        return null;
      }
      return ride.customer;
    },
});
export const vehicleSelector = selectorFamily<
  { name: string | null; licenceNumber: string | null; id: number | null } | null,
  string
>({
  key: 'tripsOverviewState_vehicle',
  get:
    (rideId: string) =>
    ({ get }) => {
      const ride: ITripsOverViewRecord | null = get(rideItemAtom(rideId));
      if (!ride) {
        return null;
      }
      return ride.vehicle;
    },
});

export const actualSizeOfRecordsAtom = atom<number>({
  key: 'tripsOverviewState_ride_book_actualSizeOfRecords',
  default: 0,
});
export const isModalFormOpenedAtom = atom<string>({
  key: 'tripsOverviewState_ride_book_isModalFormOpened',
  default: '',
});
export const tripDataAtom = atom<ITripsOverViewRecord | null>({
  key: 'tripsOverviewState_ride_book_tripData',
  default: null,
});
export const columnsAtom = atom<ITripsOverviewColumn[]>({
  key: 'tripsOverviewState_ride_book_columns',
  default: [
    {
      key: 'locked',
      default: true,
      hidden: false,
    },
    {
      key: 'timestamp',
      default: true,
      hidden: false,
    },
    {
      key: 'vehicle',

      default: true,
      hidden: false,
    },
    {
      key: 'isPrivate',

      default: false,
      hidden: false,
    },
    {
      key: 'address',

      default: false,
      hidden: false,
    },
    {
      key: 'info',

      default: false,
      hidden: false,
    },
    {
      key: 'distance',

      default: false,
      hidden: false,
    },
    {
      key: 'driver',

      default: false,
      hidden: false,
    },
    {
      key: 'duration',

      default: false,
      hidden: false,
    },
    {
      key: 'ecoScore',

      default: false,
      hidden: false,
    },
    {
      key: 'fuelConsumption',

      default: false,
      hidden: false,
    },
    {
      key: 'avgConsumption',

      default: false,
      hidden: false,
    },
    {
      key: 'parking',

      default: false,
      hidden: false,
    },
    {
      key: 'customer',

      default: false,
      hidden: true,
    },
    {
      key: 'maxSpeed',

      default: false,
      hidden: true,
    },
    {
      key: 'avgSpeed',

      default: false,
      hidden: true,
    },
    {
      key: 'mileage',

      default: false,
      hidden: true,
    },
    {
      key: 'increase',

      default: false,
      hidden: true,
    },
    {
      key: 'decline',

      default: false,
      hidden: true,
    },
    {
      key: 'source',

      default: false,
      hidden: true,
    },
    {
      key: 'co2EmissionsCan',

      default: false,
      hidden: true,
    },
    {
      key: 'actions',

      default: false,
      hidden: false,
    },
  ],
});
export const headerWidthAtom = atom<string>({
  key: 'tripsOverviewState_ride_book_headerWidth',
  default: '1450px',
});
export const advancedFiltersAtom = atom<ITripsOverviewAdvancedFilters>({
  key: 'tripsOverviewState_ride_book_daytime',
  default: {
    days: ['0', '1', '2', '3', '4', '5', '6'],
    timeFrom: new Date(new Date().setHours(0, 0, 0, 0)),
    timeTo: new Date(new Date().setHours(23, 59, 59, 0)),
    closedInterval: false,
    announcementAlarm: [],
    announcementEvent: [],
    announcementWaypoint: [],
    announcementBluecoin: [],
    ridebookPurpose: {},
    comment: false,
    waypointChoice: 'wpOff',
    alarmChoice: 'alarmOff',
    eventChoice: 'eventOff',
    purposeChoice: 'purposeOff',
    commentChoice: 'commentOff',
    bluecoinChoice: 'bluecoinOff',
    alarmChipName: null,
    eventChipName: null,
    waypointChipName: null,
    purposeChipName: null,
    commentChipName: null,
    bluecoinChipName: null,
    customerId: [],
  },
});

export const isActiveAdvanceFilterAtom = atom<IIsActiveAdvanceFilter>({
  key: 'tripsOverviewState_ride_book_isActiveAdvanceFilter',
  default: {
    days: false,
    time: false,
    announcementWaypoint: false,
    announcementEvent: false,
    announcementBluecoin: false,
    announcementAlarm: false,
    ridebookPurpose: false,
    comment: false,
  },
});
